import { useState, useEffect } from 'react';
import Container from '@cloudscape-design/components/container';
import Header from '@cloudscape-design/components/header';
import SpaceBetween from '@cloudscape-design/components/space-between';
import KeyValuePairs from '@cloudscape-design/components/key-value-pairs';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import Table from '@cloudscape-design/components/table';
import Link from '@cloudscape-design/components/link';
import Spinner from '@cloudscape-design/components/spinner';
import StatusIndicator from '@cloudscape-design/components/status-indicator';
import Alert from '@cloudscape-design/components/alert';
import { useNavigate } from 'react-router-dom';
import { getWeeklyStatusReport } from '../../api/tickets';
import { WeeklyStatusReport as WeeklyStatusData, ReportTicket } from '../../types/ticket';
import { downloadCsv, csvDateStamp } from '../../utils/csvExport';
import { TicketStatusBadge } from '../TicketStatusBadge';
import { SeverityBadge } from '../SeverityBadge';

const TICKET_COLUMNS = [
  {
    id: 'ticketId',
    header: 'Ticket',
    cell: (item: ReportTicket) => <TicketLink ticketId={item.ticketId} />,
  },
  {
    id: 'title',
    header: 'Title',
    cell: (item: ReportTicket) => item.title || '—',
  },
  {
    id: 'status',
    header: 'Status',
    cell: (item: ReportTicket) => <TicketStatusBadge status={item.status} />,
  },
  {
    id: 'severity',
    header: 'Severity',
    cell: (item: ReportTicket) => item.originalSeverity ? <SeverityBadge severity={item.originalSeverity} /> : '—',
  },
  {
    id: 'assignee',
    header: 'Assignee',
    cell: (item: ReportTicket) => item.assignee || 'Unassigned',
  },
  {
    id: 'createdAt',
    header: 'Created',
    cell: (item: ReportTicket) => item.createdAt ? new Date(item.createdAt).toLocaleDateString() : '—',
  },
];

function TicketLink({ ticketId }: { ticketId: string }) {
  const navigate = useNavigate();
  return (
    <Link onFollow={(e) => { e.preventDefault(); navigate(`/tickets/${ticketId}`); }}>
      {ticketId}
    </Link>
  );
}

export function WeeklyStatusReport() {
  const [report, setReport] = useState<WeeklyStatusData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    getWeeklyStatusReport()
      .then(setReport)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  const handleDownload = () => {
    if (!report) return;
    const rows: (string | number)[][] = [];
    for (const t of report.highSev.tickets) {
      rows.push([t.ticketId, t.title, t.status, t.originalSeverity, t.assignee, t.createdAt, 'high-sev']);
    }
    for (const t of report.stale.tickets) {
      rows.push([t.ticketId, t.title, t.status, t.originalSeverity, t.assignee, t.createdAt, 'stale']);
    }
    downloadCsv(
      `weekly-status-${csvDateStamp()}.csv`,
      ['ticketId', 'title', 'status', 'severity', 'assignee', 'createdAt', 'section'],
      rows,
    );
  };

  if (loading) return <Box textAlign="center" padding="xl"><Spinner size="large" /></Box>;
  if (error) return <Alert type="error">Failed to load report: {error}</Alert>;
  if (!report) return <Alert type="info">No report data available.</Alert>;

  const mttrOnTarget = report.mttr.current !== null && report.mttr.current <= report.mttr.target;

  return (
    <SpaceBetween size="l">
      <Container
        header={
          <Header
            variant="h2"
            description={`Period: ${report.period.start} to ${report.period.end}`}
            actions={
              <Button iconName="download" onClick={handleDownload}>
                Download CSV
              </Button>
            }
          >
            Weekly Status
          </Header>
        }
      >
        <KeyValuePairs
          columns={5}
          items={[
            { label: 'Opened This Week', value: report.opened },
            { label: 'Resolved This Week', value: report.resolved },
            { label: 'Still Open (total)', value: report.stillOpen },
            {
              label: 'Backlog Growth',
              value: (
                <StatusIndicator type={report.backlogGrowth <= 0 ? 'success' : 'warning'}>
                  {report.backlogGrowth > 0 ? `+${report.backlogGrowth}` : report.backlogGrowth}
                </StatusIndicator>
              ),
            },
            {
              label: `MTTR (target ${report.mttr.target}d)`,
              value: report.mttr.current === null ? '—' : (
                <StatusIndicator type={mttrOnTarget ? 'success' : 'error'}>
                  {report.mttr.current} days
                </StatusIndicator>
              ),
            },
          ]}
        />
      </Container>

      <Container header={<Header variant="h3" counter={`(${report.highSev.count})`}>High-Severity Open Tickets</Header>}>
        <Table
          items={report.highSev.tickets}
          columnDefinitions={TICKET_COLUMNS}
          variant="embedded"
          empty={<Box color="text-body-secondary">No open high-severity tickets 🎉</Box>}
          trackBy="ticketId"
        />
      </Container>

      <Container header={<Header variant="h3" counter={`(${report.stale.count})`} description={`Open tickets with no reply in ${report.stale.staleDays}+ days`}>Stale Tickets</Header>}>
        <Table
          items={report.stale.tickets}
          columnDefinitions={TICKET_COLUMNS}
          variant="embedded"
          empty={<Box color="text-body-secondary">No stale tickets 🎉</Box>}
          trackBy="ticketId"
        />
      </Container>

      <Box variant="small" color="text-body-secondary" textAlign="right">
        Generated: {new Date(report.generatedAt).toLocaleString()}
      </Box>
    </SpaceBetween>
  );
}
